import { useState, useRef } from 'react'
import { Download, FileSpreadsheet, FileText, ChevronDown, Loader2 } from 'lucide-react'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { ExportConfig } from '@/types'

// =============================================================================
// Export utilities
// =============================================================================

export function exportToExcel(config: ExportConfig): void {
  const headers  = config.columns.map((c) => c.header)
  const rows     = config.data.map((row) =>
    config.columns.map((c) => {
      const val = row[c.key]
      if (val === null || val === undefined) return ''
      if (typeof val === 'number') return val
      return String(val)
    })
  )

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])

  // Column widths
  ws['!cols'] = config.columns.map((c) => ({ wch: c.width ?? 20 }))

  // Header style (Excel doesn't fully support this via xlsx, but width is applied)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, config.title.slice(0, 31))
  XLSX.writeFile(wb, `${config.filename}.xlsx`)
}

export function exportToPDF(config: ExportConfig, subtitle?: string): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

  // Header
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('PSFV Navabuena — EPC Tracker', 14, 15)

  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.text(config.title, 14, 22)

  if (subtitle) {
    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text(subtitle, 14, 28)
  }

  doc.setFontSize(8)
  doc.setTextColor(150, 150, 150)
  doc.text(
    `Generado: ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
    14,
    subtitle ? 33 : 28
  )

  const startY = subtitle ? 38 : 33

  // Table
  autoTable(doc, {
    startY,
    head:    [config.columns.map((c) => c.header)],
    body:    config.data.map((row) =>
      config.columns.map((c) => {
        const val = row[c.key]
        if (val === null || val === undefined) return ''
        return String(val)
      })
    ),
    styles: {
      fontSize:  8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor:  [37, 99, 235],
      textColor:  255,
      fontStyle:  'bold',
      fontSize:   8,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    didDrawPage: (data) => {
      // Footer
      const pageCount = doc.getNumberOfPages()
      doc.setFontSize(7)
      doc.setTextColor(150, 150, 150)
      doc.text(
        `Página ${data.pageNumber} de ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 5
      )
    },
  })

  doc.save(`${config.filename}.pdf`)
}

// =============================================================================
// ExportButton Component
// =============================================================================

interface ExportOption {
  label:    string
  format:   'excel' | 'pdf'
  config:   ExportConfig
  subtitle?: string
  icon?:    React.ComponentType<{ className?: string }>
}

interface ExportButtonProps {
  options:     ExportOption[]
  disabled?:   boolean
  className?:  string
  label?:      string
}

export default function ExportButton({
  options,
  disabled  = false,
  className = '',
  label     = 'Exportar',
}: ExportButtonProps) {
  const [open, setOpen]       = useState(false)
  const [loading, setLoading] = useState<string | null>(null)
  const ref                   = useRef<HTMLDivElement>(null)

  const handleExport = async (option: ExportOption) => {
    setLoading(option.label)
    setOpen(false)

    // Defer to allow UI to update
    await new Promise((r) => setTimeout(r, 100))

    try {
      if (option.format === 'excel') {
        exportToExcel(option.config)
      } else {
        exportToPDF(option.config, option.subtitle)
      }
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        disabled={disabled || !!loading}
        onClick={() => setOpen(!open)}
        className="btn-secondary text-sm gap-2"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        {loading ? 'Exportando...' : label}
        {!loading && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-panel z-50 overflow-hidden">
            <div className="py-1">
              {options.map((opt, i) => {
                const Icon = opt.format === 'pdf' ? FileText : FileSpreadsheet
                return (
                  <button
                    key={i}
                    onClick={() => handleExport(opt)}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
                  >
                    <Icon
                      className={`w-4 h-4 shrink-0 ${
                        opt.format === 'pdf' ? 'text-red-500' : 'text-green-600'
                      }`}
                    />
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
